/**
 * accountVmBase — resolve the signed-in user's ACCOUNT VM browser-service
 * base (2026-08-18).
 *
 * Every org maps to a VM, and the custom-lane sign-in desktop belongs on
 * THAT VM when it's reachable — its persistent browser profile is the one
 * prest0n actually works from — with the platform-shared service as the
 * health-gated fallback (reliability doctrine: every rung proven alive
 * before a user sees it; the caller's POST is the final proof).
 *
 * Account VMs serve the browser-service behind Caddy at
 * `<vm baseUrl>/browser-api/*` (prefix stripped at the proxy). The vm doc
 * is the same one the realtime desktop iframe already reads
 * (`accounts/{accountId}/vm/main`, useRealtimeDesktopTarget precedent),
 * validated through the same isSafeAccountVmBase gate.
 */

import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { resolveAccount } from '../prest0nRuntime/accountResolver';
import { ACCOUNT_FIRESTORE_PREFIX } from '../prest0nRuntime/constants';
import { isSafeAccountVmBase } from '../../hooks/realtimeDesktopUrl';
import { isInternalAccount, PREST0_INC_ACCOUNT_ID } from '../../config/internalAccess';

export interface AccountVmBrowserBase {
  /** Fully qualified browser-service base on the account VM (…/browser-api). */
  base: string;
  accountId: string;
  /**
   * Per-VM browser-service token (2026-08-19 P0 hardening): the account VM's
   * `/browser-api/*` gate is provisioned with its OWN token (accounts/{id}/
   * vm/main.browserServiceToken), NOT the platform-shared token. null while
   * unprovisioned (older VMs) — the request goes out unauthenticated exactly
   * as before, and the server's allow-all default still answers.
   */
  token: string | null;
}

let cached: AccountVmBrowserBase | null | undefined;
let inflight: Promise<AccountVmBrowserBase | null> | null = null;

/**
 * Resolve the account VM's browser-service base for the current user, or
 * null when there is none to try (internal users — their VM IS the shared
 * service host; no account; no vm doc; provisioning; unsafe base). Cached
 * per page-load; never throws. Pass { fresh: true } to bypass the cache —
 * required after a wake/provision (2026-08-19): the cached null from before
 * the workstation existed would otherwise stick for the whole page load.
 */
export async function resolveAccountVmBrowserBase(
  opts?: { fresh?: boolean; accountId?: string }
): Promise<AccountVmBrowserBase | null> {
  // Explicit target (2026-08-23 warm-from-arrival): a caller that knows WHICH
  // workspace it means (the reserved/minted org during onboarding) bypasses
  // back-pointer resolution AND the page-load cache — the cache belongs to
  // the ambient resolution only.
  if (opts?.accountId) {
    try {
      const user = getAuth().currentUser;
      if (!user) return null;
      const accountId = opts.accountId;
      const db = getFirestore(getApp());
      const snap = await getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main'));
      if (!snap.exists()) return null;
      const data = snap.data() as { baseUrl?: unknown; status?: unknown; browserServiceToken?: unknown };
      if (data?.status === 'provisioning') return null;
      const baseUrl = typeof data?.baseUrl === 'string' ? data.baseUrl.trim() : '';
      if (!baseUrl || !isSafeAccountVmBase(baseUrl)) return null;
      const token = typeof data?.browserServiceToken === 'string' && data.browserServiceToken.trim()
        ? data.browserServiceToken.trim()
        : null;
      return { base: `${baseUrl.replace(/\/+$/, '')}/browser-api`, accountId, token };
    } catch {
      return null;
    }
  }
  if (opts?.fresh) {
    cached = undefined;
    inflight = null;
  }
  if (cached !== undefined) return cached;
  if (inflight) return inflight;
  inflight = (async (): Promise<AccountVmBrowserBase | null> => {
    try {
      const user = getAuth().currentUser;
      if (!user) return null;
      // Workspace-scoped internal check (2026-08-21, same fix as
      // useRealtimeDesktopTarget): an internal person rides the shared
      // internal base ONLY while their ACTIVE workspace is Prest0 Inc (or
      // resolves to nothing — internal accounts often have no profile doc).
      // In any other org they use that org's own VM like every member.
      const internal = isInternalAccount({ uid: user.uid, email: user.email ?? null });
      const resolution = await resolveAccount(user.uid);
      const accountId = resolution?.accountId ?? null;
      if (internal && (!accountId || accountId === PREST0_INC_ACCOUNT_ID)) return null;
      if (!accountId) return null;
      const db = getFirestore(getApp());
      const snap = await getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main'));
      if (!snap.exists()) return null;
      const data = snap.data() as { baseUrl?: unknown; status?: unknown; browserServiceToken?: unknown };
      if (data?.status === 'provisioning') return null;
      const baseUrl = typeof data?.baseUrl === 'string' ? data.baseUrl.trim() : '';
      if (!baseUrl || !isSafeAccountVmBase(baseUrl)) return null;
      const token = typeof data?.browserServiceToken === 'string' && data.browserServiceToken.trim()
        ? data.browserServiceToken.trim()
        : null;
      return { base: `${baseUrl.replace(/\/+$/, '')}/browser-api`, accountId, token };
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  cached = await inflight;
  return cached;
}

/**
 * Quick open-endpoint liveness probe (`GET /health` is unauthenticated on
 * the browser-service). Bounded so an unreachable VM costs ~one breath, not
 * a hang; the caller's actual POST remains the real proof of life.
 */
export async function probeBrowserServiceHealth(
  base: string,
  timeoutMs = 3500
): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${base.replace(/\/+$/, '')}/health`, {
        method: 'GET',
        signal: controller.signal,
      });
      return res.ok;
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

/** @internal — for unit tests only. */
export function __resetAccountVmBrowserBaseCacheForTests(): void {
  cached = undefined;
  inflight = null;
}
